import { createInstance } from 'i18next';
import { getLocale } from './browser';
import { en, fa } from './catalogs/web';
import { formatNumber } from './format';
import type { Locale } from './locale';

export * from './format';
export * from './locale';
export type TOptions = Record<string, string | number | boolean | undefined> & { count?: number };
type CatalogKey = keyof typeof en;
type PluralKey<K> = K extends `${infer Base}_${'one' | 'other'}` ? Base : never;
export type TranslationKey = CatalogKey | PluralKey<CatalogKey>;
const instance = createInstance();
void instance.init({
  showSupportNotice: false,
  resources: { en: { translation: en }, fa: { translation: fa } },
  keySeparator: false,
  lng: 'en',
  fallbackLng: 'en',
  initAsync: false,
  interpolation: { escapeValue: false },
  returnNull: false,
});
export const translate = (locale: Locale, key: TranslationKey, options?: TOptions): string =>
  instance.t(key, { ...options, lng: locale }) as string;
export const t = (key: TranslationKey, options?: TOptions) => translate(getLocale(), key, options);
export const createTranslator = (locale: Locale) => (key: TranslationKey, options?: TOptions) =>
  translate(locale, key, options);
export { instance as webI18n };

const sourceKeys = new Map<string, TranslationKey>(
  [...Object.entries(en), ...Object.entries(fa)].map(([key, value]) => [
    value,
    key as TranslationKey,
  ]),
);
export const translateText = (value: string, locale: Locale = getLocale()) => {
  const key = sourceKeys.get(value);
  return key ? translate(locale, key) : value;
};

export { errorMessage } from './errors';
export { enumLabel, notificationLabel, riskLabel, statusLabel } from './labels';
export { validationError } from './validation';
export const number = (value: number) => formatNumber(value, getLocale());

export { parseDateInput, serializeDateInput } from './date-input';
